import { Body } from '@assembly-js/design-system';
import { FormControl } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { FormikHelpers } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { array, object, string } from 'yup';

import { FormBodyAPI } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import {
  RecipientSelector,
  RecipientType,
} from 'src/features/recipient-selector/RecipientSelector';
import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';

export type ChannelType =
  | 'messaging'
  | 'files'
  | 'fileChannel'
  | 'forms'
  | 'contracts'
  | 'billing'
  | 'extension'
  | 'inbox';

const clientOnlyChannelTypes: ChannelType[] = ['forms', 'contracts'];

const selector = createSelector(
  (state: RootState) => state.clients.isLoading,
  (state: RootState) => state.users.isLoading,
  (isClientsStateLoading, isUsersStateLoading) => ({
    isClientsStateLoading,
    isUsersStateLoading,
  }),
);

export const useChannelForm = (channelType: ChannelType) => {
  const { t } = useTranslation();

  const baseChannelValidationSchema = object().shape({
    fields: object().shape({
      membersType: string(),
      memberIDs: array()
        .of(string())
        .when('membersType', {
          is: (membersType: string) => {
            return membersType !== 'company';
          },
          then: array().required(
            t(($) => $.validation.channels.select_at_least_one_client),
          ),
        }),
      companyID: string(),
    }),
  });

  const getValidationScheme = () => {
    let validator = Yup.object({});
    switch (channelType) {
      case 'messaging':
        validator = object({
          fields: object().shape({
            name: string(), // keep this for backward compatibility, this not needed and can be removed with EnableManyCompanies
            members: array()
              .of(string())
              .when('membersType', {
                is: (membersType: string) => membersType !== 'company',
                then: array().required(
                  t(($) => $.validation.channels.select_at_least_one_client),
                ),
              }),
            companyID: string(),
          }),
        });
        break;
      case 'forms':
        validator = object().shape({
          fields: object().shape({
            recipientIds: array()
              .of(string())
              .test(
                'clients-array',
                t(($) => $.validation.channels.select_at_least_one_client),
                (value) => value.length > 0,
              ),
            companyID: string(),
          }),
        });
        break;
      case 'fileChannel':
        validator = baseChannelValidationSchema;
        break;
      case 'extension':
        validator = baseChannelValidationSchema;
        break;
      default:
        break;
    }

    return validator;
  };

  const validationScheme = getValidationScheme();

  /**
   * text to describe the form based on the channel type
   */
  const renderInstructionText = () => {
    let text = '';
    switch (channelType) {
      case 'messaging':
        text = `Create a channel where all recipients can see each other.`;
        break;
      case 'fileChannel':
        text = 'Create a channel where all recipients can see uploaded files.';
        break;
      case 'extension':
        text = 'Create a channel where all members can see the app.';
        break;
      default:
        break;
    }
    if (text) {
      return (
        <Body size="sm" className="mb-3">
          {text}
        </Body>
      );
    }
    return null;
  };

  const FormRenderer: React.FC<FormBodyAPI & FormikHelpers<any>> = ({
    setFieldValue,
    errors,
    touched,
  }) => {
    const portalConfig = React.useContext(PortalConfigContext);

    const { isClientsStateLoading, isUsersStateLoading } =
      useAppSelector(selector);

    const renderMembersDropdown = () => {
      // if the clients or users are still loading, don't render the dropdown
      if (isClientsStateLoading || isUsersStateLoading) {
        return null;
      }

      const placeholder = t(($) => $.page_channels.select_clients_or_company);

      const recipientTypes: RecipientType[] = ['client'];
      const disableCompanies =
        portalConfig.features.disableCompanies ||
        clientOnlyChannelTypes.includes(channelType);
      if (!disableCompanies) {
        recipientTypes.push('company');
      }
      return (
        <RecipientSelector
          recipientTypes={recipientTypes}
          onRecipientSelected={(selectedRecipients) => {
            const fieldValueMap: Record<ChannelType, string> = {
              forms: 'recipientIds',
              contracts: 'recipientIds',
              billing: 'recipientIds',
              inbox: 'recipientIds',
              files: 'recipientIds',
              messaging: 'members',
              fileChannel: 'memberIDs',
              extension: 'memberIDs',
            };
            if (selectedRecipients.length === 0) {
              setFieldValue(`fields.${fieldValueMap[channelType]}`, []);
              setFieldValue('fields.membersType', '');
              setFieldValue('fields.companyID', '');
              return;
            }
            const firstRecipient = selectedRecipients.at(0);
            if (firstRecipient?.object === 'company') {
              setFieldValue('fields.companyID', firstRecipient.id);
              setFieldValue('fields.membersType', 'company');
              return;
            }
            if (firstRecipient?.object === 'client') {
              const clientIDs = selectedRecipients.map((r) => r.id);
              if (clientIDs.length > 1) {
                setFieldValue('fields.membersType', 'group');
              } else {
                setFieldValue('fields.membersType', 'individual');
              }

              setFieldValue('fields.companyID', firstRecipient.companyId);
              setFieldValue('fields.clientUserIds', [firstRecipient.id]);
              setFieldValue(`fields.${fieldValueMap[channelType]}`, clientIDs);

              if (channelType === 'messaging') {
                const clientMemberIds: string[] = [];
                selectedRecipients.forEach((val) => {
                  if (val.object === 'client') {
                    clientMemberIds.push(
                      `${portalConfig.portalHeader}_${val.id}`,
                    );
                  }
                });
                setFieldValue('fields.getstreamMembers', clientMemberIds);
              }
            }
          }}
          name="recipient"
          placeholder={placeholder}
          error={
            Boolean(
              errors?.fields?.memberIDs ||
                errors?.fields?.recipientIds ||
                errors?.fields?.members,
            ) && Boolean(touched?.fields)
          }
          helperText={
            touched?.fields
              ? errors?.fields?.memberIDs ||
                errors?.fields?.recipientIds ||
                errors?.fields?.members
              : ''
          }
        />
      );
    };

    return (
      <FormControl fullWidth>
        {renderInstructionText()}
        {renderMembersDropdown()}
      </FormControl>
    );
  };

  return { validationScheme, FormRenderer };
};
